/**
 * API v1 Service Layer
 * 
 * Centralized API service for v1 endpoints.
 * Provides type-safe API calls with automatic error handling.
 * 
 * Migration guide:
 * - Replace direct axios calls with these service methods
 * - All v1 endpoints are prefixed with /api/v1/
 * - Legacy endpoints remain at /api/ for backward compatibility
 */

import axios, { AxiosError, AxiosResponse } from 'axios';

// Base URL for API calls
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API_V1 = `${API_BASE}/api/v1`;
const API_LEGACY = `${API_BASE}/api`;

// ==================== Types ====================

export interface ApiResponse<T> {
  data: T;
  success: boolean;
  error?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  limit: number;
  skip: number;
}

// Auth types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: User;
  requires_2fa?: boolean;
  requires_2fa_setup?: boolean;
  is_sandbox?: boolean;
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  tenant_id?: string;
  profile_picture_url?: string;
  settings?: UserSettings;
  onboarding_status?: OnboardingStatus;
}

export interface UserSettings {
  min_time_increment: number;
  default_hourly_rate: number;
  notifications_enabled: boolean;
}

export interface OnboardingStatus {
  has_seen_intro_slides: boolean;
  intro_slides_completed_at?: string;
  intro_slides_skipped: boolean;
}

// Cases types
export interface Case {
  id: string;
  title?: string;
  topic?: string;
  status: string;
  client_id?: string;
  client_name?: string;
  assigned_to?: string;
  category?: string;
  complexity?: string;
  created_at: string;
  updated_at?: string;
}

export interface CaseCreate {
  title: string;
  client_id?: string;
  category?: string;
  description?: string;
}

// Clients types
export interface Client {
  id: string;
  name?: string;
  company_name?: string;
  email?: string;
  phone?: string;
  type: string;
  status: string;
  created_at: string;
}

export interface ClientCreate {
  name?: string;
  company_name?: string;
  email?: string;
  phone?: string;
  type?: string;
}

// Teams types
export interface Team {
  id: string;
  name: string;
  description?: string;
  department_id?: string;
  admin_ids: string[];
  member_ids: string[];
  created_at: string;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  profile_picture_url?: string;
}

// Capacity types
export interface CapacitySettings {
  weekly_capacity_hours: number;
  practice_area_tags: string[];
  seniority: string;
  takes_marketplace_cases: boolean;
  max_concurrent_cases: number;
  preferred_case_complexity: string[];
}

export interface Workload {
  active_cases: number;
  total_hours_this_week: number;
  pending_tasks: number;
  utilization_percent: number;
  status: 'AVAILABLE' | 'NORMAL' | 'BUSY' | 'OVERLOADED';
  can_take_new_cases: boolean;
  capacity_hours: number;
  week_start: string;
}

// Time tracking types
export interface TimeEntry {
  id: string;
  user_id: string;
  case_id?: string;
  context: string;
  date: string;
  total_seconds?: number;
  hours?: number;
  entry_type: 'auto' | 'manual';
  created_at: string;
}

export interface TimeRecordRequest {
  session_id: string;
  case_id?: string;
  context: string;
  seconds: number;
  is_final?: boolean;
}

// Invoice types
export interface Invoice {
  id: string;
  invoice_number: string;
  case_id: string;
  client_id: string;
  client_name?: string;
  subtotal: number;
  vat: number;
  total: number;
  status: string;
  issue_date: string;
  due_date: string;
}

// KYC types
export interface KYCStatus {
  case_id: string;
  kyc_active: boolean;
  status: string;
  risk_level?: string;
  last_screening?: string;
}

// Notification types
export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  created_at: string;
}

// ==================== Error Handling ====================

class ApiError extends Error {
  status: number;
  data: any;
  
  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const handleApiError = (error: AxiosError): never => {
  if (error.response) {
    const message = (error.response.data as any)?.detail || error.message;
    throw new ApiError(message, error.response.status, error.response.data);
  }
  throw new ApiError(error.message, 0);
};

// ==================== API Client ====================

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const apiClient = axios.create({
  baseURL: API_V1,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth header to all requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear auth state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==================== Auth API ====================

export const authApi = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      const response = await apiClient.post<LoginResponse>('/auth/login', credentials);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Ignore errors on logout
    }
  },

  getMe: async (): Promise<User> => {
    try {
      const response = await apiClient.get<User>('/auth/me');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getSession: async () => {
    try {
      const response = await apiClient.get('/auth/session');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  updateProfile: async (data: { name?: string; phone?: string }) => {
    try {
      const response = await apiClient.patch('/auth/profile', data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  validateToken: async (): Promise<boolean> => {
    try {
      await apiClient.get('/auth/validate');
      return true;
    } catch (error) {
      return false;
    }
  },
};

// ==================== Cases API ====================

export const casesApi = {
  list: async (params?: { status?: string; limit?: number; skip?: number }) => {
    try {
      const response = await apiClient.get('/cases', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  get: async (caseId: string): Promise<Case> => {
    try {
      const response = await apiClient.get<Case>(`/cases/${caseId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  create: async (data: CaseCreate) => {
    try {
      const response = await apiClient.post('/cases', data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  update: async (caseId: string, data: Partial<Case>) => {
    try {
      const response = await apiClient.patch(`/cases/${caseId}`, data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  delete: async (caseId: string) => {
    try {
      const response = await apiClient.delete(`/cases/${caseId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getUnified: async (caseId: string) => {
    try {
      const response = await apiClient.get(`/cases/${caseId}/unified`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// ==================== Clients API ====================

export const clientsApi = {
  list: async (params?: { status?: string; search?: string; limit?: number }) => {
    try {
      const response = await apiClient.get('/clients', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  get: async (clientId: string): Promise<Client> => {
    try {
      const response = await apiClient.get<Client>(`/clients/${clientId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  create: async (data: ClientCreate) => {
    try {
      const response = await apiClient.post('/clients', data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  update: async (clientId: string, data: Partial<Client>) => {
    try {
      const response = await apiClient.patch(`/clients/${clientId}`, data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  search: async (query: string) => {
    try {
      const response = await apiClient.get('/clients/search', { params: { q: query } });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getStats: async () => {
    try {
      const response = await apiClient.get('/clients/stats');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// ==================== Teams API ====================

export const teamsApi = {
  list: async (params?: { department_id?: string; limit?: number }) => {
    try {
      const response = await apiClient.get('/teams', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  get: async (teamId: string): Promise<Team> => {
    try {
      const response = await apiClient.get<Team>(`/teams/${teamId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  create: async (data: { name: string; description?: string; initial_members?: string[] }) => {
    try {
      const response = await apiClient.post('/teams', data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  update: async (teamId: string, data: Partial<Team>) => {
    try {
      const response = await apiClient.patch(`/teams/${teamId}`, data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  delete: async (teamId: string) => {
    try {
      const response = await apiClient.delete(`/teams/${teamId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getMembers: async (teamId: string) => {
    try {
      const response = await apiClient.get(`/teams/${teamId}/members`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  addMember: async (teamId: string, userId: string, isAdmin?: boolean) => {
    try {
      const response = await apiClient.post(`/teams/${teamId}/members`, { user_id: userId, is_admin: isAdmin });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  removeMember: async (teamId: string, userId: string) => {
    try {
      const response = await apiClient.delete(`/teams/${teamId}/members/${userId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getMyTeam: async () => {
    try {
      const response = await apiClient.get('/teams/my/team');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// ==================== Capacity API ====================

export const capacityApi = {
  getMyCapacity: async (): Promise<CapacitySettings> => {
    try {
      const response = await apiClient.get<CapacitySettings>('/capacity/me');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  updateMyCapacity: async (data: Partial<CapacitySettings>) => {
    try {
      const response = await apiClient.patch('/capacity/me', data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getMyWorkload: async (): Promise<Workload> => {
    try {
      const response = await apiClient.get<Workload>('/capacity/workload/me');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getUserWorkload: async (userId: string): Promise<Workload> => {
    try {
      const response = await apiClient.get<Workload>(`/capacity/workload/${userId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getTeamOverview: async () => {
    try {
      const response = await apiClient.get('/capacity/team/overview');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  suggestLawyerForCase: async (caseId: string) => {
    try {
      const response = await apiClient.get(`/capacity/suggest/case/${caseId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getStats: async () => {
    try {
      const response = await apiClient.get('/capacity/stats');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// ==================== Time Tracking API ====================

export const timeTrackingApi = {
  recordTime: async (data: TimeRecordRequest) => {
    try {
      const response = await apiClient.post('/time-tracking/record', data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  createManualEntry: async (data: {
    case_id: string;
    description: string;
    hours: number;
    date?: string;
    billable?: boolean;
  }) => {
    try {
      const response = await apiClient.post('/time-tracking/manual', data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getToday: async () => {
    try {
      const response = await apiClient.get('/time-tracking/today');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getWeek: async () => {
    try {
      const response = await apiClient.get('/time-tracking/week');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getCaseTime: async (caseId: string, days?: number) => {
    try {
      const response = await apiClient.get(`/time-tracking/case/${caseId}`, { params: { days } });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getReport: async (params?: { start_date?: string; end_date?: string; case_id?: string }) => {
    try {
      const response = await apiClient.get('/time-tracking/report', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getEntries: async (params?: { case_id?: string; entry_type?: string; limit?: number }) => {
    try {
      const response = await apiClient.get('/time-tracking/entries', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  deleteEntry: async (entryId: string) => {
    try {
      const response = await apiClient.delete(`/time-tracking/entries/${entryId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getStats: async (days?: number) => {
    try {
      const response = await apiClient.get('/time-tracking/stats', { params: { days } });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// ==================== Invoices API ====================

export const invoicesApi = {
  list: async (params?: { status?: string; case_id?: string; client_id?: string }) => {
    try {
      const response = await apiClient.get('/invoices', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  get: async (invoiceId: string): Promise<Invoice> => {
    try {
      const response = await apiClient.get<Invoice>(`/invoices/${invoiceId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  create: async (data: {
    case_id: string;
    client_id: string;
    line_items: Array<{ description: string; quantity: number; unit_price: number; vat_rate?: number }>;
    due_days?: number;
    notes?: string;
  }) => {
    try {
      const response = await apiClient.post('/invoices', data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  update: async (invoiceId: string, data: { status?: string; notes?: string }) => {
    try {
      const response = await apiClient.patch(`/invoices/${invoiceId}`, data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  send: async (invoiceId: string) => {
    try {
      const response = await apiClient.post(`/invoices/${invoiceId}/send`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  markPaid: async (invoiceId: string, paymentDate?: string) => {
    try {
      const response = await apiClient.post(`/invoices/${invoiceId}/mark-paid`, { payment_date: paymentDate });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  delete: async (invoiceId: string) => {
    try {
      const response = await apiClient.delete(`/invoices/${invoiceId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getStats: async () => {
    try {
      const response = await apiClient.get('/invoices/stats');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getSuggestions: async () => {
    try {
      const response = await apiClient.get('/invoices/suggestions');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// ==================== KYC API ====================

export const kycApi = {
  getStatus: async (caseId: string): Promise<KYCStatus> => {
    try {
      const response = await apiClient.get<KYCStatus>(`/kyc/case/${caseId}/status`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  activate: async (caseId: string, triggerReason?: string) => {
    try {
      const response = await apiClient.post(`/kyc/case/${caseId}/activate`, { trigger_reason: triggerReason });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  runScreening: async (caseId: string) => {
    try {
      const response = await apiClient.post(`/kyc/case/${caseId}/screen`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  approve: async (caseId: string, data: {
    run_id: string;
    decision: 'CLEAR' | 'HIT' | 'NEEDS_REVIEW';
    final_risk_level: 'LOW' | 'MEDIUM' | 'HIGH';
    notes: string;
  }) => {
    try {
      const response = await apiClient.post(`/kyc/case/${caseId}/approve`, data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getHistory: async (caseId: string) => {
    try {
      const response = await apiClient.get(`/kyc/case/${caseId}/history`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getDocuments: async (caseId: string) => {
    try {
      const response = await apiClient.get(`/kyc/case/${caseId}/documents`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getStats: async () => {
    try {
      const response = await apiClient.get('/kyc/stats');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// ==================== AI API ====================

export interface AIGenerateRequest {
  task_type?: string;
  prompt: string;
  context?: Record<string, any>;
  max_tokens?: number;
  temperature?: number;
}

export interface AITaskResult {
  content: string;
  task_type: string;
  confidence: number;
  needs_review: boolean;
  tokens_used: number;
  latency_ms: number;
}

export const aiApi = {
  generate: async (request: AIGenerateRequest): Promise<AITaskResult> => {
    try {
      const response = await apiClient.post<AITaskResult>('/ai/generate', request);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  draftEmail: async (data: {
    recipient: string;
    subject: string;
    context: string;
    tone?: 'formal' | 'friendly' | 'urgent';
  }) => {
    try {
      const response = await apiClient.post('/ai/draft-email', data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  summarizeCase: async (caseId: string, includeDocuments?: boolean) => {
    try {
      const response = await apiClient.post('/ai/summarize-case', {
        case_id: caseId,
        include_documents: includeDocuments
      });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  analyzeDocument: async (documentId: string, analysisType?: string) => {
    try {
      const response = await apiClient.post('/ai/analyze-document', {
        document_id: documentId,
        analysis_type: analysisType || 'full'
      });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  classifyCase: async (title: string, description: string) => {
    try {
      const response = await apiClient.post('/ai/classify-case', { title, description });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  draftResponse: async (caseId: string, context: string, responseType?: string) => {
    try {
      const response = await apiClient.post(`/ai/draft-response/${caseId}`, {
        case_id: caseId,
        context,
        response_type: responseType || 'email'
      });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  prepareMeeting: async (caseId: string, meetingType?: string) => {
    try {
      const response = await apiClient.post(`/ai/prepare-meeting/${caseId}`, null, {
        params: { meeting_type: meetingType }
      });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getTaskTypes: async () => {
    try {
      const response = await apiClient.get('/ai/task-types');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  healthCheck: async () => {
    try {
      const response = await apiClient.get('/ai/health');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// ==================== Notifications API ====================

export const notificationsApi = {
  list: async (params?: { unread_only?: boolean; limit?: number }) => {
    try {
      const response = await apiClient.get('/notifications', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getUnreadCount: async () => {
    try {
      const response = await apiClient.get('/notifications/unread-count');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  markAsRead: async (notificationId: string) => {
    try {
      const response = await apiClient.post(`/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  markAllAsRead: async () => {
    try {
      const response = await apiClient.post('/notifications/read-all');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  dismiss: async (notificationId: string) => {
    try {
      const response = await apiClient.post(`/notifications/${notificationId}/dismiss`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getPreferences: async () => {
    try {
      const response = await apiClient.get('/notifications/preferences');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  updatePreferences: async (preferences: Record<string, boolean>) => {
    try {
      const response = await apiClient.patch('/notifications/preferences', preferences);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// ==================== Settings API ====================

export const settingsApi = {
  getUserSettings: async () => {
    try {
      const response = await apiClient.get('/settings/user');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  updateUserSettings: async (settings: Record<string, any>) => {
    try {
      const response = await apiClient.patch('/settings/user', settings);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getTenantSettings: async () => {
    try {
      const response = await apiClient.get('/settings/tenant');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  updateTenantSettings: async (settings: Record<string, any>) => {
    try {
      const response = await apiClient.patch('/settings/tenant', settings);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getFeatures: async () => {
    try {
      const response = await apiClient.get('/settings/features');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// ==================== Activity API ====================

export const activityApi = {
  getFeed: async (params?: { limit?: number; before?: string }) => {
    try {
      const response = await apiClient.get('/activity/feed', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getSummary: async () => {
    try {
      const response = await apiClient.get('/activity/summary');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// ==================== Tasks API ====================

export const tasksApi = {
  list: async (params?: { status?: string; case_id?: string; assignee?: string; limit?: number }) => {
    try {
      const response = await apiClient.get('/tasks', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  get: async (taskId: string) => {
    try {
      const response = await apiClient.get(`/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  create: async (data: {
    title: string;
    case_id?: string;
    description?: string;
    due_date?: string;
    priority?: string;
    assignee_user_id?: string;
  }) => {
    try {
      const response = await apiClient.post('/tasks', data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  update: async (taskId: string, data: Record<string, any>) => {
    try {
      const response = await apiClient.patch(`/tasks/${taskId}`, data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  delete: async (taskId: string) => {
    try {
      const response = await apiClient.delete(`/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  complete: async (taskId: string) => {
    try {
      const response = await apiClient.post(`/tasks/${taskId}/complete`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  assign: async (taskId: string, userId: string) => {
    try {
      const response = await apiClient.post(`/tasks/${taskId}/assign`, { user_id: userId });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getOverdue: async () => {
    try {
      const response = await apiClient.get('/tasks/overdue');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getStats: async () => {
    try {
      const response = await apiClient.get('/tasks/stats');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// ==================== Documents API ====================

export const documentsApi = {
  list: async (params?: { case_id?: string; type?: string; limit?: number }) => {
    try {
      const response = await apiClient.get('/documents', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  get: async (documentId: string) => {
    try {
      const response = await apiClient.get(`/documents/${documentId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  delete: async (documentId: string) => {
    try {
      const response = await apiClient.delete(`/documents/${documentId}`);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getStats: async () => {
    try {
      const response = await apiClient.get('/documents/stats');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// ==================== Communications API ====================

export const communicationsApi = {
  list: async (params?: { case_id?: string; type?: string; limit?: number }) => {
    try {
      const response = await apiClient.get('/communications', { params });
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  send: async (data: {
    case_id: string;
    type: string;
    recipient: string;
    subject: string;
    content: string;
  }) => {
    try {
      const response = await apiClient.post('/communications/send', data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  getStats: async () => {
    try {
      const response = await apiClient.get('/communications/stats');
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },

  generateAIDraft: async (data: { case_id: string; type: string; context?: string }) => {
    try {
      const response = await apiClient.post('/communications/ai-draft', data);
      return response.data;
    } catch (error) {
      return handleApiError(error as AxiosError);
    }
  },
};

// Export all APIs
export const api = {
  auth: authApi,
  cases: casesApi,
  clients: clientsApi,
  teams: teamsApi,
  capacity: capacityApi,
  timeTracking: timeTrackingApi,
  invoices: invoicesApi,
  kyc: kycApi,
  ai: aiApi,
  notifications: notificationsApi,
  settings: settingsApi,
  activity: activityApi,
  tasks: tasksApi,
  documents: documentsApi,
  communications: communicationsApi,
};

export default api;
